<template>
  <div class="warranty-info">
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    ></Loader>
    <KTCodePreview v-bind:title="title">
      <template v-slot:toolbar>
        <b-button
          v-if="formModel.imeiCode"
          size="sm"
          variant="outline-primary"
          @click="onShowWarrantyImeiHistory"
        >
          <i class="fa fa-history mr-1"></i>
          Lịch sử bảo hành
        </b-button>
      </template>
      <template v-slot:preview>
        <LoadingSpinner
          v-if="isLoadingComponent"
          :message="'Đang tải dữ liệu...'"
        />
        <template v-else>
          <WarrantyCustomerInfoSection
            :warranty="formModel"
            :warranty-service-options="warrantyServiceOptions"
            :is-edit-mode="isEditMode"
            :can-update-service-type="canUpdateWarrantyServiceType"
            @update-field="updateField"
            @customer-found="onCustomerFound"
            @customer-cleared="onCustomerCleared"
          />

          <WarrantyProductWarrantyInfoSection
            :warranty="formModel"
            :option-stores="optionStores"
            :inspected-by-employee-name="inspectedByEmployeeName"
            :is-edit-mode="isEditMode"
            :is-create-from-bill="false"
            :dp-configs="dpConfigs"
            :show-expected-return-date="false"
            @update-field="updateField"
            @select-store="onSelectStore"
            @imei-selected="onImeiSelected"
            @product-selected="onProductSelected"
            @employee-selected="onInspectedByEmployeeSelected"
          />

          <b-row class="mt-4">
            <b-col cols="12">
              <b-form-group class="required-control">
                <label class="font-weight-bold">Lý do từ chối</label>
                <b-form-textarea
                  rows="4"
                  max-rows="6"
                  size="sm"
                  :value="formModel.rejectReason"
                  @input="updateField('rejectReason', $event)"
                  placeholder="Nhập lý do từ chối..."
                />
              </b-form-group>
            </b-col>
          </b-row>
        </template>
      </template>

      <template v-slot:foot>
        <SaveButton
          v-if="!isEditMode"
          :is-busy="isLoading || isLoadingComponent"
          text="Tạo phiếu"
          variant="primary"
          size="sm"
          button-class="font-weight-bolder mr-4"
          @click="onSubmit"
        >
          <i class="fas fa-save mr-1"></i>
          Lưu
        </SaveButton>
        <b-button
          size="sm"
          variant="secondary"
          class="font-weight-bolder"
          @click="cancel"
        >
          <i class="fas fa-arrow-left mr-1"></i>
          Hủy
        </b-button>
        <template>
          <b-modal
            ref="modal-warranty-imei-history"
            hide-footer
            title="Lịch sử bảo hành"
            id="modal-warranty-imei-history-rejection"
            size="xl"
          >
            <WarrantyImeiHistory :imeiCode="formModel.imeiCode" />
          </b-modal>
        </template>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store//modules/breadcrumbs.module';
import { mapGetters } from 'vuex';
import Loader from '@/view/content/Loader.vue';
import timeUtils from '@/utils/date';
import {
  createWarrantyRejection,
  findOneReception,
  getWarrantyServices,
} from './utils/warrantyApi';
import { handleError, makeToastFaile } from '@/utils/common';
import { formatDBDate, convertToDateSlash, DATE_FORMAT } from '@/utils/date';
import permissionMixin from '@/view/mixins/permission.mixin';
import apiCommonMixin from '@/view/mixins/api-common.mixins';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import WarrantyCustomerInfoSection from './components/WarrantyCustomerInfoSection.vue';
import WarrantyProductWarrantyInfoSection from './components/WarrantyProductWarrantyInfoSection.vue';
import WarrantyImeiHistory from './components/ImeiWarrantyHistory.vue';
import { formatSpecificDate, CLIENT_DATE_TIME_NO_SECONDS } from '@/utils/date';

export default {
  mixins: [permissionMixin, apiCommonMixin],
  data() {
    return {
      isEditMode: false,
      title: 'Tạo phiếu từ chối bảo hành',
      isLoadingComponent: false,
      isLoading: false,
      formModel: {
        warrantyServiceId: null,
        customerId: null,
        customerName: '',
        customerPhone: '',
        customerContactNumber: '',
        customerAddress: '',
        customerEmail: '',
        storeId: null,
        storeName: '',
        productId: '',
        productName: '',
        imeiCode: '',
        purchaseDate: null,
        inspectedByEmployeeId: null,
        rejectReason: '',
      },
      warrantyServiceOptions: [],
      inspectedByEmployeeName: '',
      optionStores: [],
      dpConfigs: {
        date: timeUtils.DP_CONFIG.date,
        datetime: timeUtils.DP_CONFIG_DATETIME.date,
      },
    };
  },
  components: {
    KTCodePreview,
    Loader,
    LoadingSpinner,
    WarrantyCustomerInfoSection,
    WarrantyProductWarrantyInfoSection,
    WarrantyImeiHistory,
  },
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
    canUpdateWarrantyServiceType() {
      return (
        this.checkPermission('WARRANTY_UPDATE_SERVICE_TYPE') ||
        this.checkPermission('WARRANTY_UPDATE_UNLIMIT')
      );
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.title }]);
    this.fetchWarrantyServices();
    this.fetchStores();
    if (this.$route.query.id) {
      this.isEditMode = true;
      this.getWarrantyRejectionInfo();
    }
  },
  methods: {
    updateField(field, value) {
      if (this.formModel[field] === value) return;
      this.formModel = { ...this.formModel, [field]: value };
    },
    onCustomerFound(customerData) {
      const updates = {};
      if (customerData.customerName && !this.formModel.customerName) {
        updates.customerName = customerData.customerName;
      }
      if (customerData.customerEmail && !this.formModel.customerEmail) {
        updates.customerEmail = customerData.customerEmail;
      }
      if (customerData.customerAddress && !this.formModel.customerAddress) {
        updates.customerAddress = customerData.customerAddress;
      }
      if (customerData.customerId && !this.formModel.customerId) {
        updates.customerId = customerData.customerId;
      }
      if (Object.keys(updates).length) {
        this.formModel = { ...this.formModel, ...updates };
      }
    },
    onCustomerCleared() {
      this.updateField('customerName', '');
      this.updateField('customerEmail', '');
      this.updateField('customerAddress', '');
      this.updateField('customerId', null);
    },
    onInspectedByEmployeeSelected(employee) {
      const employeeId = employee ? employee.id : null;
      this.inspectedByEmployeeName = employee ? employee.name : '';
      this.updateField('inspectedByEmployeeId', employeeId);
    },
    onSelectStore(node) {
      const storeId = node && node.id ? node.id : null;
      const storeName = node && node.label ? node.label : null;
      this.formModel = { ...this.formModel, storeId, storeName };
    },
    async onImeiSelected(productData) {
      if (!productData) return;
      const updates = {
        imeiCode: productData.imeiCode || '',
        productName: productData.productName || '',
        purchaseDate: formatSpecificDate(
          productData.latestBillCreatedAt,
          CLIENT_DATE_TIME_NO_SECONDS,
          DATE_FORMAT,
        ),
      };
      if (productData.productId) updates.productId = productData.productId;
      this.formModel = { ...this.formModel, ...updates };
    },
    onProductSelected(payload) {
      const { productId = null, productName = '' } = payload || {};
      this.formModel = {
        ...this.formModel,
        productId,
        productName,
      };
    },
    async fetchWarrantyServices() {
      try {
        const services = await getWarrantyServices();
        this.warrantyServiceOptions = services.filter((s) => s.isActive === 1);
      } catch (err) {
        handleError(err);
      }
    },
    async fetchStores() {
      try {
        const { stores, currentStoreId, currentStoreName } = await this.fetchStoreByUser();
        this.optionStores = this.mapDataTreeSelect(stores, ['id', 'name', 'shortName']);
        if (!this.$route.query.id && currentStoreId) {
          this.formModel.storeId = currentStoreId;
          this.formModel.storeName = currentStoreName;
        }
      } catch (err) {
        handleError(err);
      }
    },
    async getWarrantyRejectionInfo() {
      this.isLoadingComponent = true;
      try {
        const id = this.$route.query.id;
        const result = await findOneReception(id);
        if (result) {
          this.formModel = {
            id: result.id,
            code: result.code,
            warrantyServiceId: result.warrantyServiceId,
            customerId: result.customerId,
            customerName: result.customerName || '',
            customerPhone: result.customerPhone || '',
            customerContactNumber: result.customerContactNumber || '',
            customerAddress: result.customerAddress || '',
            customerEmail: result.customerEmail || '',
            storeId: result.storeId,
            storeName: result.storeName || '',
            productId: result.productId,
            productName: result.productName || '',
            imeiCode: result.imeiCode || '',
            purchaseDate: convertToDateSlash(result.purchaseDate, DATE_FORMAT),
            inspectedByEmployeeId: result.inspectedByEmployeeId,
            rejectReason: result.rejectReason || result.cancelReason || '',
          };
          this.inspectedByEmployeeName = result.inspectedByEmployeeName || '';
        }
      } catch (error) {
        handleError(error);
      } finally {
        this.isLoadingComponent = false;
      }
    },
    mapDataTreeSelect(items = [], keys = []) {
      if (!items || items.length === 0) return [];
      const [idKey, nameKey, shortNameKey] = keys;
      return items.map((item) => ({
        id: item[idKey],
        label: item[nameKey] || item[shortNameKey],
        shortName: item[shortNameKey] || item[nameKey],
      }));
    },
    validate() {
      const errors = [];
      if (!this.formModel.warrantyServiceId) errors.push('Yêu cầu chọn loại dịch vụ');
      if (!this.formModel.customerPhone) errors.push('Yêu cầu nhập SĐT khách hàng');
      if (!this.formModel.customerName) errors.push('Yêu cầu nhập tên khách hàng');
      if (!this.formModel.productName) errors.push('Yêu cầu nhập tên sản phẩm');
      if (!this.formModel.imeiCode) errors.push('Yêu cầu nhập IMEI');
      if (!this.formModel.rejectReason || !String(this.formModel.rejectReason).trim())
        errors.push('Yêu cầu nhập lý do từ chối');
      return errors;
    },
    async onSubmit() {
      const errors = this.validate();
      if (errors.length) {
        errors.forEach((e) => makeToastFaile(e));
        return;
      }
      this.isLoading = true;
      try {
        const payload = {
          warrantyServiceId: this.formModel.warrantyServiceId,
          storeId: this.formModel.storeId,
          customerId: this.formModel.customerId,
          customerPhone: this.formModel.customerPhone,
          customerContactNumber: this.formModel.customerContactNumber,
          customerAddress: this.formModel.customerAddress,
          customerEmail: this.formModel.customerEmail,
          customerName: this.formModel.customerName,
          productId: this.formModel.productId,
          productName: this.formModel.productName,
          imeiCode: this.formModel.imeiCode,
          purchaseDate: formatDBDate(this.formModel.purchaseDate),
          inspectedByEmployeeId: this.formModel.inspectedByEmployeeId,
          rejectReason: this.formModel.rejectReason,
          accompanyingEquipments: [],
        };
        await createWarrantyRejection(payload);
        this.$router.push({ name: 'warranty-rejections' });
      } finally {
        this.isLoading = false;
      }
    },
    cancel() {
      this.$router.back();
    },
    onShowWarrantyImeiHistory() {
      this.$refs['modal-warranty-imei-history'].show();
    },
  },
};
</script>

<style lang="scss" scoped>
.warranty-info {
  .bootstrap-datetimepicker-widget.dropdown-menu {
    width: 21em;
  }
}
</style>

